import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { switchMap } from 'rxjs';
import { EntityService } from 'src/app/core/services/entity.service';
import { FitService } from 'src/app/core/services/fit.service';
import { SettingsService } from 'src/app/core/services/settings.service';
import { Settings360Depict } from 'src/app/core/models/settings.model';
import { User } from 'src/app/core/models/user.model';
import { Groups } from 'src/app/core/models/group.model';
import * as utils from 'src/app/shared/helpers/utils';

/** Maps Settings360Depict technology keys to frozen URL parameter values. thinmaxNegative excluded (no frozen example). */
const FROZEN_TECH_MAP: { key: keyof NonNullable<Settings360Depict['technologies']>; frozen: string }[] = [
  { key: 'thinmaxPositive', frozen: 'thinmax' },
  { key: 'highPrescriptions', frozen: 'high' },
  { key: 'extremePrescriptions', frozen: 'extreme' },
  { key: 'curvedStyle', frozen: 'curve' },
];

@Component({
  selector: 'hbo-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {

  user: User = utils.localStorageGetItem('user');
  mimesysAvailable = false;
  fitAvailable = false;
  depict360Available = false;
  firstFrozenTech: string | null = null;

  cardSettings: any = null;

  private readonly groups = new Groups();

  private get canAccessDashboard(): boolean {
    return (this.user?.roleId === 1 && this.user?.profileId === 3)
      || this.user?.roleId === 4;
  }

  constructor(
    private entityService: EntityService,
    private fitService: FitService,
    private settingsService: SettingsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (!this.canAccessDashboard) {
      this.router.navigate(['/entities']);
      return;
    }

    this.settingsService.get(this.user.entityId, 0).subscribe({
      next: (res: any) => { this.cardSettings = res?.dashboard ?? null; },
      error: (err: any) => { console.error('[DashboardComponent] Failed to load settings', err); }
    });

    // Admin and owner see all cards
    if (this.user.roleId === 1 && this.user.profileId === 3) {
      this.mimesysAvailable = true;
      this.fitAvailable = true;
      this.depict360Available = true;

      this.loadFrozenTech();
      return;
    }
    // Opticals: cards come from their actual licences
    this.entityService.getEntityApps(this.user.entityId).subscribe(res => {
      const appsIds: number[] = res.appsIds ?? [];
      this.mimesysAvailable = appsIds.some((id: number) => this.groups.mimesys.includes(id));
      // app ids, not packageType ids: app 5 = FIT, app 7 = 360Depict
      this.fitAvailable = appsIds.includes(5);
      // The card is the STANDALONE product: Fit Depict also holds app 7, but bundled with
      // Fit, and it opens from the fit results instead of from here.
      this.depict360Available = (res.standaloneAppsIds ?? []).includes(7);
      if (!this.groups.showDashboard(res)) {
        this.router.navigate(['/entities']);
        return;
      }
      this.loadFrozenTech();
    });
  }

  goToGazeMaps(): void {
    this.router.navigate(['/gaze-maps']);
  }

  goToFits(): void {
    this.router.navigate(['/fits']);
  }

  open360Depict(): void {
    this.fitService.open360Depict();
  }

  open360DepictFrozen(): void {
    if (this.firstFrozenTech) {
      this.fitService.open360DepictFrozen(this.firstFrozenTech);
    }
  }

  private loadFrozenTech(): void {
    if (!this.depict360Available) return;

    const entity = this.user.entity;

    this.entityService.getAncestor(entity).pipe(
      switchMap(res => {
        const parentEntity = res.parentEntity;
        const hierarchyObj = {
          hierarchy: {
            shop_id: String(entity.internalCode || entity.id),
            ancestor: entity.entityTypeId !== 1
              ? {
                shop_id: String(parentEntity?.internalCode || parentEntity?.id),
                ancestor: entity.entityTypeId !== 2
                  ? {
                    shop_id: String(parentEntity?.parentEntity?.internalCode || parentEntity?.parentEntity?.id),
                    ancestor: parentEntity?.parentEntity?.entityTypeId === 2
                      ? {
                        shop_id: String(parentEntity?.parentEntity?.parentEntity?.internalCode || parentEntity?.parentEntity?.parentEntity?.id),
                        ancestor: null
                      }
                      : null
                  }
                  : null
              }

              : null
          }
        };
        const hierarchy = utils.objectToBase64(hierarchyObj);

        return this.settingsService.getOrCreateThickness(
          entity.internalCode || entity.id, hierarchy, entity.entityTypeId, entity.timezone
        );
      })
    ).subscribe({
      next: (settings: Settings360Depict) => {
        if (!settings.technologies) return;
        const match = FROZEN_TECH_MAP.find(t => settings.technologies![t.key]?.enabled);
        if (match) {
          this.firstFrozenTech = match.frozen;
        }
      },
      error: (err: any) => { console.error('[DashboardComponent] Failed to load 360Depict technologies', err); }
    });
  }
}
